import { useApiClient } from './apiClient';
import { BundleReservations } from './utils/BundlingReservation';

export const useReservationApis = () => {
  const { get, post, putR, customDelete } = useApiClient();

  const fetchReservationBundles = async (date) => {
    try {
      const data = await get(`reservations/withDetails/byDate?date=${date}`);
      
      if (!data || !Array.isArray(data)) {
        console.error('API returned invalid data:', data);
        return { checkOutData: [], checkInData: [] };
      }

      const checkOutData = data.filter(reservation => reservation.status === 'PENDING');
      const checkInData = data.filter(reservation => reservation.status === 'CHECKED_OUT');

      const userRole = sessionStorage.getItem('userRole');
      const userId = sessionStorage.getItem('userId');

      if (userRole === 'ROLE_ADMIN' || userRole === 'ROLE_STAFF') {
        return BundleReservations(checkOutData, checkInData);
      } else if (userRole === 'ROLE_STUDENT') {
        const userCheckOut = checkOutData.filter(r => String(r.userId) === userId);
        const userCheckIn = checkInData.filter(r => String(r.userId) === userId);
        return BundleReservations(userCheckOut, userCheckIn);
      }

      return BundleReservations([], []);
    } catch (error) {
      console.error('Error in fetchReservationBundles:', error);
      return { checkOutData: [], checkInData: [] };
    }
  };

  const fetchReservations = async (date) => {
    try {
      const data = await get(`reservations/withDetails/byDate?date=${date}`);
      
      if (!data || !Array.isArray(data)) {
        console.error('API returned invalid data:', data);
        return { checkOutData: [], checkInData: [] };
      }

      const checkOutData = data.filter(r => r.status === 'PENDING');
      const checkInData = data.filter(r => r.status === 'CHECKED_OUT');

      const userRole = sessionStorage.getItem('userRole');
      const userId = sessionStorage.getItem('userId');

      if (userRole === 'ROLE_ADMIN' || userRole === 'ROLE_STAFF') {
        return { checkOutData, checkInData };
      } else if (userRole === 'ROLE_STUDENT') {
        const userCheckOutData = checkOutData.filter(r => String(r.userId) === userId);
        const userCheckInData = checkInData.filter(r => String(r.userId) === userId);
        return {
          checkOutData: userCheckOutData,
          checkInData: userCheckInData,
        };
      }
    } catch (error) {
      console.error('Error in fetchReservations:', error);
      return { checkOutData: [], checkInData: [] };
    }
  };

  const fetchAvailableTimes = async (date, assetTag) => {
    try {
      return await get(`reservations/available-slots?date=${date}&assetTag=${assetTag}`);
    } catch (err) {
      console.error("Failed to fetch available slots:", err);
      return [];
    }
  };

  const cancelReservation = async (reservationId) => {
    try {
      return await customDelete(`api/reservations/${reservationId}`);
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const updateReservation = async (reservation, reservationId) => {
    try {
      return await putR(`api/reservations/${reservationId}`, reservation);
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const addMultipleReservations = async (reservations) => {
    try {
      const data = await post('api/reservations/add-multiple', reservations);
      
      // Check if this is an availability error response
      if (data.success === false && data.error === "Some assets are no longer available") {
        return {
          success: false,
          message: data.message || 'Some assets are no longer available',
          unavailableAssets: data.unavailableAssets || [],
          isAvailabilityError: true
        };
      }
      
      const { reservation, emailStatus } = data;

      if (reservation) {
        return {
          success: true,
          reservation,
          emailStatus,
          partialSuccess: emailStatus !== 'Sent'
        };
      } else {
        return {
          success: false,
          message: data?.error || 'Unknown error occurred'
        };
      }
    } catch (err) {
      console.error('Request failed:', err);
      return {
        success: false,
        message: err.message
      };
    }
  };

  // Convenience method that handles both single and multiple reservations
  const addReservation = async (reservationData) => {
    const reservationsArray = Array.isArray(reservationData) ? reservationData : [reservationData];
    return addMultipleReservations(reservationsArray);
  };

  const addMultipleStudentReservations = async (studentId, reservations) => {
    try {
      const data = await post(`api/reservations/add-studentmode?studentId=${studentId}`, reservations);
      
      // Check if this is an availability error response
      if (data.success === false && data.error === "Some assets are no longer available") {
        return {
          success: false,
          message: data.message || 'Some assets are no longer available',
          unavailableAssets: data.unavailableAssets || [],
          isAvailabilityError: true
        };
      }
      
      const { reservation, emailStatus, studentExist } = data;

      if (reservation) {
        return {
          studentExist,
          success: true,
          reservation,
          emailStatus,
          partialSuccess: emailStatus !== 'Sent'
        };
      } else {
        return {
          studentExist,
          success: false,
          message: data?.error || 'Unknown error occurred'
        };
      }
    } catch (err) {
      console.error('Request failed:', err);
      return {
        success: false,
        message: err.message
      };
    }
  };

  // Convenience method that handles both single and multiple student reservations
  const addStudentReservation = async (studentId, reservationData) => {
    const reservationsArray = Array.isArray(reservationData) ? reservationData : [reservationData];
    return addMultipleStudentReservations(studentId, reservationsArray);
  };

  const fetchAvailableItems = async (date, time, assetTags) => {
    try {
      const tagParams = assetTags.join(",");
      return await get(
        `reservations/available-items?date=${date}&time=${time}&assetTags=${tagParams}`
      );
    } catch (err) {
      console.error("Failed to fetch available items:", err);
      return [];
    }
  };

  const sendReservationConfirmationEmail = async (reservation) => {
    try {
      return await post(`api/reservations/send-confirmation`, reservation);
    } catch (err) {
      console.error("Failed to send reservation confirmation:", err);
      return false;
    }
  };

  const fetchMaxExtensionTime = async (reservationId) => {
    try {
      const data = await get(`api/reservations/${reservationId}/max-extension`);
      return {
        success: true,
        data: data
      };
    } catch (err) {
      console.error("Failed to fetch max extension time:", err);
      return {
        success: false,
        error: err.response?.data?.message || err.message || "Failed to fetch extension information"
      };
    }
  };

  return {
    fetchReservationBundles,
    fetchReservations,
    fetchAvailableTimes,
    cancelReservation,
    updateReservation,
    addReservation,
    addMultipleReservations,
    addStudentReservation,
    addMultipleStudentReservations,
    fetchAvailableItems,
    sendReservationConfirmationEmail,
    fetchMaxExtensionTime
  };
};
